import { useEffect, useMemo, useState } from "react";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { pageOverheadBucketRoots } from "@/lib/manufacturing/overheadBucketTaxonomy";
import type { MfgOverheadPeriodBucketRow } from "@/lib/manufacturing/overheadTypes";
import { OverheadPeriodValueEditor } from "./OverheadPeriodValueEditor";
import { OverheadPeriodValueFolder } from "./OverheadPeriodValueFolder";
import type { useManufacturingOverheadPeriodValues } from "./useManufacturingOverheadPeriodValues";

const PAGE_SIZES = [5, 10, 15] as const;
type PageSize = (typeof PAGE_SIZES)[number];
type PeriodValuesState = ReturnType<typeof useManufacturingOverheadPeriodValues>;
type Group = { key: string; folder: MfgOverheadPeriodBucketRow | null; items: MfgOverheadPeriodBucketRow[] };

function groupRows(rows: MfgOverheadPeriodBucketRow[]): Group[] {
  const byId = new Map(rows.map((row) => [row.bucket_id, row]));
  const children = new Map<string, MfgOverheadPeriodBucketRow[]>();
  const childIds = new Set<string>();
  for (const row of rows) {
    if (!row.parent_bucket_id || !byId.has(row.parent_bucket_id)) continue;
    childIds.add(row.bucket_id);
    const list = children.get(row.parent_bucket_id) ?? [];
    list.push(row);
    children.set(row.parent_bucket_id, list);
  }
  const groups: Group[] = [];
  for (const row of rows) {
    if (childIds.has(row.bucket_id)) continue;
    const items = children.get(row.bucket_id) ?? [];
    if (row.is_aggregate || items.length > 0) groups.push({ key: row.bucket_id, folder: row, items });
    else groups.push({ key: row.bucket_id, folder: null, items: [row] });
  }
  return groups;
}

export function OverheadPeriodValueList({ state }: { state: PeriodValuesState }) {
  const { summary, plantId, periodMonth } = state;
  const groups = useMemo(() => groupRows(summary?.rows ?? []), [summary?.rows]);
  const [pageSize, setPageSize] = useState<PageSize>(5);
  const [pageIndex, setPageIndex] = useState(0);
  const page = pageOverheadBucketRoots(groups, pageIndex, pageSize);

  useEffect(() => {
    setPageIndex(0);
  }, [plantId, periodMonth]);

  return (
    <>
      <ul className="space-y-2">
        {page.page.map((group) => (
          <li key={`${group.key}:${plantId}:${periodMonth}`}>
            {group.folder ? (
              <OverheadPeriodValueFolder folder={group.folder} items={group.items} state={state} />
            ) : group.items[0] ? (
              <OverheadPeriodValueEditor row={group.items[0]} state={state} />
            ) : null}
          </li>
        ))}
      </ul>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600 dark:text-zinc-400">
        <span>{page.total === 0 ? "0 buckets" : `${page.rangeStart}–${page.rangeEnd} of ${page.total}`}</span>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1">
            <span>Show</span>
            <select
              aria-label="Period values page size"
              className="rounded-lg border border-gray-200 bg-white px-2 py-1 dark:border-zinc-700 dark:bg-zinc-900"
              value={pageSize}
              onChange={(event) => {
                const next = Number(event.target.value);
                if (next === 5 || next === 10 || next === 15) setPageSize(next);
                setPageIndex(0);
              }}
            >
              {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </label>
          {page.pageCount > 1 ? (
            <>
              <button type="button" className={ghostBtnClass} disabled={page.pageIndex <= 0} onClick={() => setPageIndex(page.pageIndex - 1)}>Previous</button>
              <span>{page.pageIndex + 1} / {page.pageCount}</span>
              <button type="button" className={ghostBtnClass} disabled={page.pageIndex + 1 >= page.pageCount} onClick={() => setPageIndex(page.pageIndex + 1)}>Next</button>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}
